import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { normalizeCdtStudioCategoryKey } from "@CommonApi/merchant-settings";
import { PrimitiveAtom, useAtom, useAtomValue } from "jotai";

import { Highlights } from "./panels/Highlights/Highlights";
import { ProductDetails } from "./panels/ProductDetails/ProductDetails";
import { UnrangedItems } from "./panels/UnrangedItems/UnrangedItems";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { unrangedShelfIdAtom } from "./panels/UnrangedItems/store/atoms";
import { useEffect } from "react";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { SidepanelButton } from "./panels/NewShelf/components/SidepanelButton";
import { ShelvesDetails } from "./panels/ShelvesDetails/ShelvesDetails";
import { NewShelf } from "./panels/NewShelf/NewShelf";
import { SpaceMix } from "./panels/SpaceMix/SpaceMix";
import { UnrangedContainers } from "./panels/UnrangedContainers/UnrangedContainers";
import { SidepanelToggleButton } from "./components/ToggleButton";
import { SidepanelLockButton } from "./components/LockButton";
import { CdtStudioPanel, type OpenGenerate } from "src/pages/CdtStudio/components/CdtStudioPanel";
import { getStudioCategoryConfigurationDebugInfo } from "src/pages/CdtStudio/store/studioCategoryConfiguration";
import { useCdtStudioAvailability } from "src/pages/CdtStudio/store/useCdtStudioAvailability";
import { unsupportedPegboardPlacementsOf } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import { useUser } from "src/atoms/user";
import {
  planogramSidepanelIsExpandedAtom,
  planogramSidepanelTabsLockedAtom,
  usePlanogramPanel,
} from "./store/hooks";
import { ProductsDetails } from "./panels/ProductsDetails/ProductsDetails";
import { planogramSelectedItemsAtom } from "src/components/PlanogramExtensions/store/atoms";
import { merchantSettingsAtom } from "src/components/FeatureFlagProvider/store/atoms";
import { useSetShelfDividersByHand } from "./panels/ShelvesDetails/useSetShelfDividersByHand";

interface Props {
  scaleX: number;
  scaleY: number;
  pogAtom: PrimitiveAtom<Pog>;
  isEditable: boolean;
  isPog: boolean;
  merchflowId?: number;
  basePogId?: number;
  currentStoreCode?: string;
  showProducts?: boolean;
  onGenerate?: OpenGenerate;
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null;
  inspectionOnly?: boolean;
  fillViewport?: boolean;
}

export const PlanogramPanel = ({
  scaleX,
  scaleY,
  pogAtom,
  isEditable,
  isPog,
  merchflowId,
  basePogId,
  currentStoreCode,
  availableStores,
  showProducts = true,
  onGenerate,
  inspectionOnly = false,
  fillViewport = false,
}: Props) => {
  const { t } = useTranslation("common");
  const [pog, setPog] = useAtom(pogAtom);
  const { tab, setTab } = usePlanogramPanel();
  const setShelfDividersByHand = useSetShelfDividersByHand({
    merchflowId,
    storeCode: isPog ? currentStoreCode : undefined,
    setPog,
  });
  const { user } = useUser();
  const merchantSettings = useAtomValue(merchantSettingsAtom);
  const {
    availability: cdtStudioAvailability,
    categoryIdentity,
    studioCategoryConfiguration,
    canEditCdt,
  } = useCdtStudioAvailability({
    isPog,
    merchflowId,
    availableStores,
    hasUnsupportedFixtures: pog ? unsupportedPegboardPlacementsOf([pog]).length > 0 : false,
  });
  const showCdtStudio = cdtStudioAvailability.isAvailable;
  const cdtStudioUnavailableReason = cdtStudioAvailability.isAvailable
    ? undefined
    : cdtStudioAvailability.reason;
  const cdtStudioUnavailableMessage = cdtStudioUnavailableReason
    ? t(`cdtStudioTab.unavailable.${cdtStudioUnavailableReason}`)
    : undefined;

  useEffect(() => {
    if (inspectionOnly || !isPog || cdtStudioUnavailableReason === undefined) return;
    console.warn("[CDT Studio][disabled]", {
      reason: cdtStudioUnavailableReason,
      merchflowId,
      merchantId: user.merchantId,
      merchantName: user.merchantName,
      canEditCdt,
      categoryIdentity: categoryIdentity ?? null,
      categoryCandidates:
        availableStores?.stores.map(({ category_code, retailer_category_code }) => ({
          categoryGroup: retailer_category_code,
          categoryCode: category_code,
        })) ?? [],
      configuration: categoryIdentity
        ? getStudioCategoryConfigurationDebugInfo(categoryIdentity)
        : undefined,
    });
  }, [
    availableStores,
    canEditCdt,
    categoryIdentity?.categoryCode,
    categoryIdentity?.categoryGroup,
    cdtStudioUnavailableReason,
    isPog,
    inspectionOnly,
    merchflowId,
    user.merchantId,
    user.merchantName,
  ]);

  const [unrangedShelfId] = useAtom(unrangedShelfIdAtom);
  const [selectedItems] = useAtom(planogramSelectedItemsAtom);

  const [isExpanded, setIsExpanded] = useAtom(planogramSidepanelIsExpandedAtom);
  const [isLocked, setIsLocked] = useAtom(planogramSidepanelTabsLockedAtom);
  const isCdtStudioOpen =
    isExpanded &&
    tab === "CDT_STUDIO" &&
    showCdtStudio &&
    categoryIdentity !== undefined &&
    studioCategoryConfiguration !== undefined;

  useEffect(() => {
    if (unrangedShelfId) {
      setTab("UNRANGED_ITEMS");
    }
  }, [unrangedShelfId, setTab]);

  useEffect(() => {
    if (tab === "PRODUCT_DETAILS" && selectedItems.length === 0) {
      setTab("UNRANGED_ITEMS", true);
    }
  }, [tab, selectedItems, setTab]);

  const hasProductSelection = selectedItems.length > 0;
  const contentWidth = tab === "CDT_STUDIO" ? "clamp(560px, 48vw, 720px)" : "322px";

  if (inspectionOnly)
    return (
      <Flex
        minWidth="0"
        minHeight="0"
        height="100%"
        width="100%"
        data-testid="planogram-product-inspector"
      >
        <fieldset
          disabled
          aria-label="Product information"
          onClickCapture={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onChangeCapture={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onKeyDownCapture={(event) => {
            if (event.key !== "Tab" && !event.ctrlKey && !event.metaKey) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          style={{
            display: "flex",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            border: 0,
            padding: 0,
            margin: 0,
          }}
        >
          <ProductDetails pog={pog} setPog={setPog} readOnly />
        </fieldset>
        <Flex column>
          <SidepanelButton
            tab="PRODUCT_DETAILS"
            setTab={setTab}
            currentTab="PRODUCT_DETAILS"
            isDisabled={!hasProductSelection}
          >
            Product
          </SidepanelButton>
        </Flex>
      </Flex>
    );

  return (
    <Flex
      data-testid="planogram-panel"
      width={fillViewport ? (isExpanded ? `calc(${contentWidth} + 28px)` : "28px") : undefined}
      flexShrink={fillViewport ? 0 : undefined}
      minHeight={fillViewport ? "0" : undefined}
      height={fillViewport ? "100%" : undefined}
      overflow={fillViewport ? "hidden" : undefined}
    >
      {isExpanded && (
        <Flex
          data-testid="planogram-tab-content"
          flexGrow
          borderLeft={`1px solid ${Color.primary}`}
          width={contentWidth}
          flexShrink={fillViewport ? 0 : undefined}
          minWidth="0"
          minHeight={fillViewport ? "0" : undefined}
          height={fillViewport ? "100%" : undefined}
          overflow={fillViewport ? "hidden" : undefined}
        >
          {isCdtStudioOpen && (
            <CdtStudioPanel
              key={`${categoryIdentity.merchantId}/${normalizeCdtStudioCategoryKey(categoryIdentity.categoryGroup)}/${normalizeCdtStudioCategoryKey(categoryIdentity.categoryCode)}`}
              pogAtom={pogAtom}
              isEditable={isEditable}
              merchflowId={merchflowId}
              basePogId={basePogId}
              categoryIdentity={categoryIdentity}
              categoryConfiguration={studioCategoryConfiguration}
              onClose={() => setTab("HIGHLIGHTS")}
              onGenerate={onGenerate}
            />
          )}

          {tab === "UNRANGED_ITEMS" && (
            <UnrangedItems
              pogAtom={pogAtom}
              scaleX={scaleX}
              scaleY={scaleY}
              merchflowId={merchflowId}
              currentStoreCode={currentStoreCode}
              availableStores={availableStores}
            />
          )}

          {tab === "UNRANGED_CONTAINERS" && <UnrangedContainers pogAtom={pogAtom} />}

          {tab === "PRODUCT_DETAILS" &&
            (selectedItems.length > 1 ? (
              <ProductsDetails pog={pog} setPog={setPog} />
            ) : (
              <ProductDetails pog={pog} setPog={setPog} />
            ))}

          {tab === "HIGHLIGHTS" && <Highlights merchflowId={merchflowId} pogAtom={pogAtom} />}

          {tab === "FIXTURES_DETAILS" && (
            <ShelvesDetails
              pogAtom={pogAtom}
              isShelfWeightEnabled={Boolean(merchantSettings?.shelfWeightEnabled)}
              onSetDividersByHand={setShelfDividersByHand}
            />
          )}

          {tab === "ADD_EDIT_FIXTURE" && <NewShelf pogAtom={pogAtom} />}

          {tab === "CDT_STUDIO" && !showCdtStudio && isPog && (
            <Flex column gap="8px" padding="16px">
              <Text variant="h5">{t("cdtStudioTab.tabLabel")}</Text>
              <Text variant="body3" color={Color.textSecondary}>
                {cdtStudioUnavailableMessage}
              </Text>
            </Flex>
          )}

          {tab === "SPACE_MIX" && <SpaceMix pogAtom={pogAtom} isPog={isPog} />}
        </Flex>
      )}

      <Flex
        width="28px"
        minWidth="28px"
        column
        align="between"
        borderLeft={`1px solid ${Color.primary}`}
        background={Color.greenSmokeDisabled}
        data-tour="sidepanel-tabs"
        flexShrink={fillViewport ? 0 : undefined}
        minHeight={fillViewport ? "0" : undefined}
        height={fillViewport ? "100%" : undefined}
        overflow={fillViewport ? "auto" : undefined}
      >
        <Flex column>
          {isPog && showProducts && (
            <>
              <SidepanelButton
                tab="PRODUCT_DETAILS"
                setTab={setTab}
                currentTab={tab}
                isDisabled={!hasProductSelection}
              >
                Product
              </SidepanelButton>

              <SidepanelButton tab="UNRANGED_ITEMS" setTab={setTab} currentTab={tab}>
                Unranged Items
              </SidepanelButton>

              <SidepanelButton tab="HIGHLIGHTS" setTab={setTab} currentTab={tab}>
                Highlights
              </SidepanelButton>
            </>
          )}

          {!isPog && showProducts && (
            <SidepanelButton tab="UNRANGED_CONTAINERS" setTab={setTab} currentTab={tab}>
              Unranged Containers
            </SidepanelButton>
          )}

          {showProducts && (
            <SidepanelButton tab="SPACE_MIX" setTab={setTab} currentTab={tab}>
              Space Mix
            </SidepanelButton>
          )}

          <SidepanelButton tab="FIXTURES_DETAILS" setTab={setTab} currentTab={tab}>
            Fixtures
          </SidepanelButton>

          <SidepanelButton tab="ADD_EDIT_FIXTURE" setTab={setTab} currentTab={tab}>
            Add / Edit Fixture
          </SidepanelButton>

          {isPog && (
            <SidepanelButton
              tab="CDT_STUDIO"
              setTab={setTab}
              currentTab={tab}
              isDisabled={!showCdtStudio}
              disabledReason={cdtStudioUnavailableMessage}
              tourId="open-cdt-studio"
            >
              {t("cdtStudioTab.tabLabel")}
            </SidepanelButton>
          )}
        </Flex>

        <Flex column>
          <SidepanelLockButton isLocked={isLocked} setIsLocked={setIsLocked} />
          <SidepanelToggleButton isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
        </Flex>
      </Flex>
    </Flex>
  );
};
